import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloneIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AuthContext } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import ConfirmDialog from "../../../components/common/ConfirmDialog/ConfirmDialog";
import {
  useCloneSystemTemplate,
  useDeleteSummaryTemplate,
  useHideSystemTemplate,
  useSetDefaultTemplate,
  useSummaryTemplates,
  useUnhideSystemTemplate,
} from "../../../hooks/useSummaryTemplates";
import type { SummaryTemplate } from "../../../types/summaryTemplate/SummaryTemplate";

// System templates are identified by customerId === "system"
const isSystemTemplate = (t: SummaryTemplate) => t.customerId === "system";
const isPersonalTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId != null;
const isCustomerTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId == null;

interface TemplateRowProps {
  template: SummaryTemplate;
  canManage: boolean;
  isOwn: boolean;
  onEdit: (id: string) => void;
  onDelete: (template: SummaryTemplate) => void;
  onSetDefault: (id: string) => void;
  onClone?: (id: string) => void;
  onHide?: (id: string) => void;
  onUnhide?: (id: string) => void;
  borderColor: string;
}

const TemplateRow = ({
  template,
  canManage,
  isOwn,
  onEdit,
  onDelete,
  onSetDefault,
  onClone,
  onHide,
  borderColor,
}: TemplateRowProps) => {
  const { t } = useTranslation("summaryTemplate");
  const system = isSystemTemplate(template);

  return (
    <ListItem
      sx={{ borderLeft: `4px solid ${borderColor}`, mb: 1, bgcolor: "background.paper", borderRadius: 1 }}
      secondaryAction={
        <Stack direction="row" spacing={0.5} alignItems="center">
          {template.isDefault && (
            <Chip
              label={t("default")}
              color="primary"
              size="small"
              icon={<StarIcon />}
              sx={{ fontWeight: 700 }}
            />
          )}
          {system && onClone && (canManage || isOwn) && (
            <Tooltip title={t("clone")}>
              <IconButton size="small" onClick={() => onClone(template.id)}>
                <CloneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {system && onHide && canManage && (
            <Tooltip title={t("hide")}>
              <IconButton size="small" onClick={() => onHide(template.id)}>
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!system && (canManage || isOwn) && (
            <>
              <Tooltip title={t("edit")}>
                <IconButton size="small" onClick={() => onEdit(template.id)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!template.isDefault && (
                <Tooltip title={t("setDefault")}>
                  <IconButton size="small" onClick={() => onSetDefault(template.id)}>
                    <StarIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t("delete")}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(template)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      }
    >
      <ListItemText
        primary={template.name}
        secondary={`${template.outputFormat} · ${template.lengthGuidance}`}
      />
    </ListItem>
  );
};

interface HiddenSystemRowProps {
  template: SummaryTemplate;
  onUnhide: (id: string) => void;
}

const HiddenSystemRow = ({ template, onUnhide }: HiddenSystemRowProps) => {
  const { t } = useTranslation("summaryTemplate");
  return (
    <ListItem
      sx={{ borderLeft: "4px solid #bdbdbd", mb: 1, bgcolor: "grey.50", borderRadius: 1, opacity: 0.6 }}
      secondaryAction={
        <Tooltip title={t("unhide")}>
          <IconButton size="small" onClick={() => onUnhide(template.id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <ListItemText
        primary={template.name}
        secondary={`${template.outputFormat} · ${template.lengthGuidance}`}
        primaryTypographyProps={{ sx: { textDecoration: "line-through", color: "text.disabled" } }}
      />
    </ListItem>
  );
};

interface SectionGroupProps {
  title: string;
  hint: string;
  borderColor: string;
  children: React.ReactNode;
}

const SectionGroup = ({ title, hint, borderColor, children }: SectionGroupProps) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ color: borderColor }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Stack>
    <List dense disablePadding>
      {children}
    </List>
  </Box>
);

const SummaryTemplateList = () => {
  const { t } = useTranslation("summaryTemplate");
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const showAlert = useAlert();

  const canManage = user?.hasPermission("summary-templates:manage") ?? false;

  const { data, isLoading } = useSummaryTemplates(customerId);
  const deleteMutation = useDeleteSummaryTemplate(customerId);
  const setDefaultMutation = useSetDefaultTemplate(customerId);
  const hideMutation = useHideSystemTemplate(customerId);
  const unhideMutation = useUnhideSystemTemplate(customerId);
  const cloneMutation = useCloneSystemTemplate(customerId);

  const [deleteTarget, setDeleteTarget] = useState<SummaryTemplate | null>(null);

  const templates = data?.templates ?? [];
  const hiddenSystemTemplateIds = data?.hiddenSystemTemplateIds ?? [];
  const systemVisible = templates.filter(
    (t) => isSystemTemplate(t) && !hiddenSystemTemplateIds.includes(t.id),
  );
  const systemHidden = templates.filter(
    (t) => isSystemTemplate(t) && hiddenSystemTemplateIds.includes(t.id),
  );
  const customerTemplates = templates.filter(isCustomerTemplate);
  const personalTemplates = templates.filter(isPersonalTemplate);

  const handleEdit = (id: string) => navigate(`${id}/edit`);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showAlert(t("deleteSuccess"), "success");
    } catch {
      showAlert(t("deleteError", { ns: "errors" }), "error");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultMutation.mutateAsync(id);
      showAlert(t("defaultSuccess"), "success");
    } catch {
      showAlert(t("deleteError", { ns: "errors" }), "error");
    }
  };

  const handleHide = async (id: string) => {
    try {
      await hideMutation.mutateAsync(id);
      showAlert(t("hideSuccess"), "success");
    } catch {
      showAlert(t("deleteError", { ns: "errors" }), "error");
    }
  };

  const handleUnhide = async (id: string) => {
    try {
      await unhideMutation.mutateAsync(id);
      showAlert(t("unhideSuccess"), "success");
    } catch {
      showAlert(t("deleteError", { ns: "errors" }), "error");
    }
  };

  const handleClone = async (systemTemplateId: string) => {
    try {
      await cloneMutation.mutateAsync({
        systemTemplateId,
        request: { scope: "personal" },
      });
      showAlert(t("cloneSuccess"), "success");
    } catch {
      showAlert(t("deleteError", { ns: "errors" }), "error");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="flex m-auto flex-col h-3/4">
      <Card className="flex flex-col">
        <CardHeader
          title={t("title")}
          subheader={t("description")}
        />
        <CardContent className="flex-1 overflow-y-auto">
          {/* System Templates */}
          <SectionGroup
            title={t("systemTemplates")}
            hint={t("systemTemplatesHint")}
            borderColor="#1976d2"
          >
            {systemVisible.map((tpl) => (
              <TemplateRow
                key={tpl.id}
                template={tpl}
                canManage={canManage}
                isOwn={false}
                borderColor="#1976d2"
                onEdit={handleEdit}
                onDelete={setDeleteTarget}
                onSetDefault={handleSetDefault}
                onClone={handleClone}
                onHide={handleHide}
              />
            ))}
            {/* Hidden system templates (unhide only visible to managers) */}
            {canManage &&
              systemHidden.map((tpl) => (
                <HiddenSystemRow key={tpl.id} template={tpl} onUnhide={handleUnhide} />
              ))}
          </SectionGroup>

          <Divider sx={{ mb: 3 }} />

          {/* Customer Templates */}
          <SectionGroup
            title={t("customerTemplates")}
            hint={t("customerTemplatesHint")}
            borderColor="#388e3c"
          >
            {customerTemplates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                {t("noCustomerTemplates")}
              </Typography>
            ) : (
              customerTemplates.map((tpl) => (
                <TemplateRow
                  key={tpl.id}
                  template={tpl}
                  canManage={canManage}
                  isOwn={false}
                  borderColor="#388e3c"
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                  onSetDefault={handleSetDefault}
                />
              ))
            )}
          </SectionGroup>

          <Divider sx={{ mb: 3 }} />

          {/* Personal Templates */}
          <SectionGroup
            title={t("myTemplates")}
            hint={t("myTemplatesHint")}
            borderColor="#f57c00"
          >
            {personalTemplates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                {t("noPersonalTemplates")}
              </Typography>
            ) : (
              personalTemplates.map((tpl) => (
                <TemplateRow
                  key={tpl.id}
                  template={tpl}
                  canManage={canManage}
                  isOwn={tpl.userId === user?.userId}
                  borderColor="#f57c00"
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                  onSetDefault={handleSetDefault}
                />
              ))
            )}
          </SectionGroup>
        </CardContent>

        <Box className="sticky bottom-0 bg-white h-fit z-50">
          <Divider />
          <Stack className="p-2" direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("new")}
            >
              {t("newTemplate")}
            </Button>
          </Stack>
        </Box>
      </Card>

      <ConfirmDialog
        isOpen={deleteTarget != null}
        setIsOpen={(open) => { if (!open) setDeleteTarget(null); }}
        title={t("delete")}
        action={t("deleteConfirm")}
        confirmText={t("delete")}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default SummaryTemplateList;
